import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LayoutGrid, SlidersHorizontal } from 'lucide-react';
import { API_BASE_URL } from '../config';
import PredictionCard from '../components/analysts/PredictionCard';
import PredictionFilters from '../components/analysts/PredictionFilters';
import Pagination from '../components/Pagination';
import ListLimitBanner from '../components/ui/ListLimitBanner';

const PAGE_SIZE = 40;

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PredictionsPage() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    direction: '',
    platform: '',
    ordering: '-published_at',
  });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [predictionsListLimit, setPredictionsListLimit] = useState(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => { setPage(1); }, [filters]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.direction) params.set('direction', filters.direction);
    if (filters.platform) params.set('channel__platform', filters.platform);
    if (filters.ordering) params.set('ordering', filters.ordering);
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    axios
      .get(`${API_BASE_URL}/api/research/predictions/?${params}`, { headers: getAuthHeader() })
      .then((res) => {
        const data = res.data;
        if (data && 'results' in data) {
          setPredictions(data.results);
          setTotalCount(data.count ?? 0);
          if (data.list_limit_reached) {
            setPredictionsListLimit({ limit: data.list_limit, tier: data.list_limit_tier });
          } else {
            setPredictionsListLimit(null);
          }
        } else {
          setPredictions(Array.isArray(data) ? data : []);
          setTotalCount(Array.isArray(data) ? data.length : 0);
          setPredictionsListLimit(null);
        }
      })
      .catch(() => { setPredictions([]); setTotalCount(0); setPredictionsListLimit(null); })
      .finally(() => setLoading(false));
  }, [filters, page]);

  const activeFilterCount = [filters.direction, filters.platform].filter(Boolean).length;

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Predictions</h1>
        <p className="text-sm text-gray-500 mt-1">
          Track market calls from top analysts, measured against real outcomes.
        </p>

      </div>

      <div className="mb-5">
        {/* Mobile filter toggle */}
        <div className="flex md:hidden items-center justify-between mb-2">
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              activeFilterCount > 0
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            <SlidersHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-white text-gray-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filters — always visible on md+, toggleable on mobile */}
        <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>
          <PredictionFilters filters={filters} onChange={setFilters} />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse h-36" />
          ))}
        </div>
      ) : predictions.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <LayoutGrid size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No predictions found.</p>
          <p className="text-sm mt-1">Try adjusting the filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {predictions.map((pred) => (
            <PredictionCard key={pred.id} prediction={pred} />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPage={setPage} />
      {predictionsListLimit && (
        <ListLimitBanner limit={predictionsListLimit.limit} type="prediction" />
      )}
    </div>
  );
}
